import { desktop, mobile, tablet } from "components/templates/Breakpoints";
import styled from "styled-components";
import { Asterisk } from "./styles";
import Image from "next/legacy/image";
import DropdownIcon from "../../../public/static/icons/DropdownIcon.svg";
import CloseIcon from "../../../public/static/icons/PinkClose.svg";
import { color } from "@styles";
import { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from "react";
import { useField } from "@unform/core";

export interface Option {
  name: string;
  id: string;
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  options: Option[];
}

export const MultiSelectInput = ({ placeholder, options, ...rest }: InputProps) => {
  const inputRef = useRef(null);
  const ref = useRef(null);

  const { fieldName, registerField, error, clearError } = useField(rest.name);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: (ref) => {
        ref.current.value = "";
      },
    });
  }, [fieldName, registerField]);

  const handleOptions = () => {
    setShowOptions((currentOptions) => !currentOptions);
  };

  const handleItem = useCallback(
    (e, option: Option) => {
      if (error) {
        clearError();
      }
      e.preventDefault();
      if (!!selectedOptions.find((thisOption) => thisOption.id === option.id)) {
        setSelectedOptions(selectedOptions.filter((thisOption) => thisOption.id !== option.id));
      } else {
        setSelectedOptions((currentOptions) => [option, ...currentOptions]);
      }
    },
    [error, selectedOptions, clearError],
  );

  const handleClear = useCallback(
    (e, optionId) => {
      if (error) {
        clearError();
      }
      e.preventDefault();
      setSelectedOptions(selectedOptions.filter((thisOption) => thisOption.id !== optionId));
    },
    [clearError, error, selectedOptions],
  );

  const handleValue = (options: Option[]) => {
    return options.map((option) => option.id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        showOptions && setShowOptions(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [showOptions]);

  return (
    <Relative ref={ref}>
      <ArrowContainer isOptionsHidden={!showOptions}>
        <Image src={DropdownIcon} alt="" />
      </ArrowContainer>
      <StyledInput
        $hasError={!!error}
        ref={inputRef}
        {...rest}
        value={handleValue(selectedOptions)}
        autoComplete="off"
        spellCheck="false"
        onClick={handleOptions}
      />
      <Options isHidden={!showOptions}>
        {options.map((option) => (
          <OptionItem
            key={`optionItemKey-${option.id}`}
            isSelected={!!selectedOptions.find((currentOption) => currentOption.id === option.id)}
            onClick={(e) => handleItem(e, { id: option.id, name: option.name })}
          >
            {option.name}
          </OptionItem>
        ))}
      </Options>
      <InputLabelWrapper>
        <InputLabel htmlFor={rest.name}>
          {selectedOptions.length > 0 ? (
            <OptionsTagContainer>
              {selectedOptions.map((option) => (
                <OptionTag key={`optionKey-${option.id}`}>
                  {option.name}
                  <ClearContainer onClick={(e) => handleClear(e, option.id)}>
                    <Image width={10} height={10} src={CloseIcon} alt="" />
                  </ClearContainer>
                </OptionTag>
              ))}
            </OptionsTagContainer>
          ) : (
            <PlaceholderContainer onClick={handleOptions} isOpened={showOptions}>
              {placeholder}
              <Asterisk />
            </PlaceholderContainer>
          )}
        </InputLabel>
        <SmoothGradientEnd />
      </InputLabelWrapper>
      {!!error ? <ErrorText>{error}</ErrorText> : null}
    </Relative>
  );
};

const Relative = styled.div`
  position: relative;
  align-self: flex-start;
  width: 100%;
`;

const ErrorText = styled.div`
  color: #ff5f5f;
  margin: -0.5rem 0 0.5rem 0;
`;

const StyledInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  margin: 1rem 0;
  box-sizing: border-box;
  padding: 1rem 1.25rem 1rem 0;
  font-weight: normal;
  font-size: 1.25rem;
  color: ${color.B1100};
  border-radius: 0.5rem;
  border: 2px solid ${(p) => (p.$hasError ? "#FF5F5F" : color.B240)};
  cursor: pointer;
  color: transparent;

  ${mobile} {
    margin: 0.5rem 0;
  }

  &::placeholder {
    color: ${color.B2100};
  }
`;

const OptionTag = styled.div`
  display: flex;
  align-items: center;
  color: ${color.B1100};
  border-radius: 0.5rem;
  background-color: ${color.P10};
  font-size: 0.9rem;
  padding: 0.5rem;
  margin-right: 0.3rem;
  white-space: nowrap;
  position: relative;
  z-index: 2;
`;

const ClearContainer = styled.div`
  border-radius: 5000px;
  margin-left: 0.3rem;
  width: 1.2rem;
  height: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: ${color.P20};
  }
`;

const SmoothGradientEnd = styled.div`
  width: 3rem;
  height: 100%;
  background: rgba(255, 255, 255, 0);
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 1) 100%);
  position: absolute;
  top: -0.4rem;
  right: 0;
  z-index: 3;
  pointer-events: none;

  ${mobile} {
    height: 130%;
    top: -1rem;
  }
`;

const OptionsTagContainer = styled.div`
  position: relative;
  display: flex;
  overflow-x: auto;
  padding: 0.05rem 0rem;
  max-width: 100%;
  margin-top: -0.4rem;

  ${mobile} {
    margin-top: -1rem;
  }
`;

const Options = styled.ul<{ isHidden: boolean }>`
  display: ${(props) => (props.isHidden ? "none" : "initial")};
  border: 2px solid ${color.B230};
  background-color: white;
  padding: 0.5rem 0;
  position: absolute;
  border-radius: 5px;
  width: 100%;
  top: 85px;
  left: 0;
  z-index: 2;
  cursor: pointer;
  max-height: 20rem;
  overflow-y: scroll;
`;

const OptionItem = styled.li<{ isSelected: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  margin: 0.25rem 0;
  color: ${color.B1100};
  background-color: ${({ isSelected }) => (isSelected ? color.P10 : "transparent")};
  :hover {
    background-color: ${({ isSelected }) => (isSelected ? color.P20 : color.B220)};
  }
`;

const ArrowContainer = styled.div<{ isOptionsHidden: boolean }>`
  position: absolute;
  right: 0;
  padding: 1rem 1.25rem;
  pointer-events: none;
  display: flex;
  align-items: center;
  height: 6rem;
  transform: ${({ isOptionsHidden }) => (isOptionsHidden ? "initial" : "rotate(180deg)")};

  ${mobile} {
    height: 5rem;
  }
`;

const InputLabelWrapper = styled.div<{ invisible?: boolean }>`
  width: 100%;
  max-width: calc(100% - 4.3rem);
  margin: 0.9rem 0;
  position: absolute;
  top: 0.95rem;
  left: 1rem;
  display: flex;
  align-items: center;
  visibility: ${(p) => (p.invisible ? "hidden" : "visible")};
  ${tablet} {
    max-width: calc(100 % - 2.5rem);
    top: 1rem;
  }

  &:hover {
    div {
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
      }
    }
  }

  div {
    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar {
      height: 0.4rem;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0);
      border-radius: 5px;

      &:hover {
        background-color: rgba(0, 0, 0, 0.4);
      }
    }
  }
`;

const PlaceholderContainer = styled.div<{ isOpened?: boolean }>`
  line-height: 2.5rem;
  cursor: pointer;

  margin-top: ${(p) => (p.isOpened ? "-0.5rem" : "0rem")};
  white-space: ${(p) => (p.isOpened ? "initial" : "nowrap")};
  text-overflow: ${(p) => (p.isOpened ? "nowrap" : "ellipsis")};
  overflow: ${(p) => (p.isOpened ? "initial" : "hidden")};
  line-height: 2.3rem;

  ${desktop} {
    margin-top: 0rem;
  }

  ${tablet} {
    line-height: ${(p) => (p.isOpened ? "1.6rem" : "2.3rem")};
  }

  ${mobile} {
    line-height: 1.3rem;
  }
`;

const InputLabel = styled.label<{ invisible?: boolean }>`
  visibility: ${(p) => (p.invisible ? "hidden" : "visible")};
  color: ${color.B2100};
  font-weight: normal;
  font-size: 1.25rem;
  width: 100%;
`;
